/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { toast } from "react-hot-toast";
import { Html5Qrcode } from "html5-qrcode";
import DataTable from "react-data-table-component";
import ReactDatePicker from "react-datepicker";

/* MATERIAL TAILWIND COMPONENTS */
import { Button, Card, CardBody } from "@material-tailwind/react";

/* APIs */
import { getScannerDetailsById, postScannerDetails } from "service/api";
import Input from "components/input";
import moment from "moment";

const ScannerTool = () => {
  /* LOADING STATES */
  const [loading, setLoading] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  /* SCANNER RESULT */
  const [scanner, setScanner] = useState(null);

  /* PAGINATION */
  const [page, setPage] = useState(1);
  const [sizePerPage, setSizePerPage] = useState(50);

  /* TABLE DATA */
  const [visitData, setVisitData] = useState([]);
  const [totalDocs, setTotalDocs] = useState([]);

  /* FILTERS */
  const today = new Date();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [name, setName] = useState("");

  /* START SCANNER */
  const startScanner = () => {
    const html5QrCode = new Html5Qrcode("reader");
    setScanner(html5QrCode);
    setIsScanning(true);

    let hasScanned = false;

    const qrCodeSuccessCallback = async (decodedText) => {
      if (hasScanned) return;
      hasScanned = true;

      try {
        await handleScannerAPI(decodedText);
      } catch (err) {
        console.error("Error handling scanned data:", err);
      }

      setTimeout(() => {
        html5QrCode
          .stop()
          .then(() => {
            console.log("Scanner stopped after successful scan");
            setIsScanning(false);
          })
          .catch((err) => {
            console.warn("Stop error:", err?.message || err);
          });
      }, 300);
    };

    const config = { fps: 10, qrbox: { width: 250, height: 195 } };

    html5QrCode
      .start({ facingMode: "environment" }, config, qrCodeSuccessCallback)
      .catch((error) => {
        console.error("Error starting scanner:", error);
      });
  };

  /* STOP SCANNER */
  const stopScanner = () => {
    if (scanner && isScanning) {
      scanner
        .stop()
        .then(() => {
          console.log("Scanner stopped");
          setIsScanning(false);
        })
        .catch((err) => {
          console.error("Error stopping scanner:", err);
        });
    } else if (!isScanning) {
      console.log("Cannot stop, scanner is not running or paused");
    }
  };

  /* HANDLE SCANNER API */
  const handleScannerAPI = async (value) => {
    postScannerDetails({ token: value })
      .then((res) => {
        if (res?.status) {
          toast.success(res?.message);
          setErrorMessage(res?.message);
          getVisitLists();
        }
      })
      .catch((e) => {
        setLoading(false);
        console.log("Error: ", e?.response?.data?.message);
        if (e?.response?.data?.message) {
          toast.error(e?.response?.data?.message);
          setErrorMessage(e?.response?.data?.message);
        } else {
          toast.error(e?.message);
          setErrorMessage(e?.message);
        }
      })
      .finally((res) => {
        console.log("Completed");
        // Scanner is already stopped in qrCodeSuccessCallback, no need to stop again
      });
  };

  /* GET LIST OF VISITS */
  const getVisitLists = () => {
    setLoading(true);
    setVisitData([]);
    const params = {
      page: page,
      sizePerPage: sizePerPage,
      pagination: true,
    };

    if (selectedDate !== null) {
      params.startDate = moment(selectedDate).format("YYYY-MM-DD");
      params.endDate = moment(selectedDate).format("YYYY-MM-DD");
    }

    if (name !== "") {
      params.search = name;
    }

    getScannerDetailsById("66ba8a7dac99be2d925e11bf", params)
      .then((res) => {
        setVisitData(res?.data?.docs);
        setTotalDocs(res?.data?.totalDocs);
      })
      .catch((e) => {
        console.log("Error: ", e);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  /* COLUMNS */
  const columns = [
    {
      name: "Sr. No",
      grow: 0.5,
      selector: (row, index) => (
        <span className="text-sm font-ibm-semibold text-black text-opacity-60">
          {index + 1}
        </span>
      ),
    },
    {
      name: "Name",
      grow: 1,
      selector: (row) => (
        <p className="text-sm font-ibm-semibold text-black">
          {`${row?.userId?.firstName} ${row?.userId?.lastName}`}
        </p>
      ),
    },
    {
      name: "Email and Phone",
      grow: 1.5,
      selector: (row) => (
        <div>
          <a
            href={`mailto:${row?.personalDetails?.userId?.email}`}
            className="mt-1 block text-sm font-ibm-regular text-greyText hover:text-logoYellow"
          >
            {row?.userId?.email}
          </a>
          <a
            href={`tel:${row?.personalDetails?.userId?.phoneNumber}`}
            className="mt-1 block text-sm font-ibm-regular text-greyText hover:text-logoYellow"
          >
            {row?.userId?.phoneNumber}
          </a>
        </div>
      ),
    },
    {
      name: "Visit count",
      grow: 1,
      selector: (row) => (
        <p className="text-sm font-ibm-semibold text-black">
          {`${row?.visitCount}`}
        </p>
      ),
    },
    {
      name: "Date",
      grow: 1,
      selector: (row) => (
        <>
          <p className="text-sm font-ibm-regular text-greyText">
            {new Date(row.date).toLocaleDateString("en-gb", {
              year: "numeric",
              month: "short",
              day: "numeric",
            })}
          </p>
        </>
      ),
    },
  ];

  /* LOADING SCREEN FOR DATATABLE */
  const LoadingScreen = () => {
    return (
      <div className="w-full bg-[#F5F5F5] bg-opacity-50 rounded-md border border-authBorder py-5">
        <p className="text-sm font-ibm-semibold text-black text-center">
          Please wait!
        </p>
        <p className="text-sm font-ibm-semibold text-black text-center">
          We are fetching data{" "}
        </p>
      </div>
    );
  };

  /* NO DATA SCREEN */
  const NoDataScreen = () => {
    return (
      <div className="w-full bg-[#F5F5F5] bg-opacity-50 rounded-md border border-authBorder py-5">
        <p className="text-sm font-ibm-semibold text-black text-center">
          There are no queries to display.
        </p>
      </div>
    );
  };

  /* HANDLE PER PAGE CHANGE */
  const handlePerPageChange = async (e) => {
    setSizePerPage(e);
    getVisitLists();
  };

  /* HANDLE PAGE CHANGE */
  const handlePageChange = async (e) => {
    setPage(e);
  };

  /* USE EFFECT */
  useEffect(() => {
    getVisitLists();
  }, [page, sizePerPage, name, selectedDate]);

  return (
    <section className="px-0 py-2 xl:p-5 lg:p-5">
      {/* PAGE TITLE */}
      <Helmet>
        <title>Scanner | LIBF</title>
      </Helmet>

      <div className="container-full-width">
        {/* HEADING */}
        <div className="block">
          <h1 className="text-2xl font-ibm-bold text-black tracking-tight">
            Delegate Pass Scanner
          </h1>
          <h2 className="text-base font-ibm-regular text-greyText">
            Scan the QR code of the delegates to get their details
          </h2>
        </div>

        {/* SCANNER DETAILS */}
        <Card className="mt-5 bg-white shadow border border-greyBorder rounded-md">
          <CardBody className="p-3 xl:p-5 lg:p-5">
            <div className="block xl:flex lg:flex items-center">
              <div className="pr-0 xl:pr-5 lg:pr-5 border-0 xl:border-r lg:border-r border-greyBorder">
                <div
                  id="reader"
                  className={`w-[250px] h-[195px] border-2 border-[#E5E5E5] ${
                    isScanning ? "block" : "hidden"
                  }`}
                ></div>

                <div className={`${isScanning ? "hidden" : "block"}`}>
                  <img
                    src={require("../../../assets/images/scan.png")}
                    className="w-32 h-32 object-contain"
                    alt="Scanner"
                  />
                </div>
              </div>
              <div className="mt-3 xl:mt-0 lg:mt-0 pl-0 xl:pl-5 lg:pl-5">
                <h5 className="text-lg font-ibm-bold text-black">
                  Scan QR Code for Entry
                </h5>
                <p className="mt-1 text-base font-ibm-regular text-greyText">
                  Click Below to Scan the QR Code for attendee verification.
                </p>
                <div className="mt-3 block xl:flex lg:flex items-center gap-3">
                  {!isScanning ? (
                    <Button
                      onClick={startScanner}
                      className="w-full xl:w-fit lg:w-fit shadow-none hover:shadow-none bg-logoYellow hover:bg-opacity-70 transition-all duration-300 ease-in-out rounded-md normal-case font-ibm-semibold text-sm"
                    >
                      Scan QR code
                    </Button>
                  ) : (
                    <Button
                      onClick={stopScanner}
                      className="w-full xl:w-fit lg:w-fit shadow-none hover:shadow-none bg-logoYellow hover:bg-opacity-70 transition-all duration-300 ease-in-out rounded-md normal-case font-ibm-semibold text-sm"
                    >
                      Stop scanning
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* FILTERS */}
        <div className="mt-5 block xl:flex lg:flex items-center gap-5">
          {/* SEARCHBAR BY CITY */}
          <div className="w-full xl:w-[30vh] lg:w-[30vh]">
            <Input
              type="text"
              placeholder="Search by name, email or phone"
              name="name"
              id="name"
              value={name}
              onChange={(e) => setName(e?.target?.value)}
            />
          </div>
          <div className="mt-3 xl:mt-0 lg:mt-0 flex items-center gap-3">
            <p className="text-base font-ibm-regular text-greyText flex-shrink-0">
              Filter by date:
            </p>
            <div className="w-full xl:w-[20vh] lg:w-[20vh]">
              <ReactDatePicker
                selected={selectedDate}
                onChange={(date) => setSelectedDate(date)}
                minDate={today}
                dateFormat="dd-MM-yyyy"
                dayClassName={(date) =>
                  date < today.setHours(0, 0, 0, 0)
                    ? "disabled-date"
                    : undefined
                }
              />
            </div>
          </div>
        </div>

        {/* TABLE */}
        <div className="mt-5">
          <DataTable
            className="data-table"
            columns={columns}
            data={totalDocs > 0 ? visitData : []}
            progressPending={loading}
            progressComponent={<LoadingScreen />}
            pagination
            paginationServer
            paginationTotalRows={totalDocs}
            onChangeRowsPerPage={handlePerPageChange}
            onChangePage={handlePageChange}
            noDataComponent={<NoDataScreen />}
            rowsPerPage={sizePerPage}
            paginationRowsPerPageOptions={[50, 100]}
            paginationPerPage={sizePerPage}
            defaultSortFieldId={1}
          />
        </div>
      </div>
    </section>
  );
};

export default ScannerTool;
